import { useState } from 'react';
import type { App } from '@hangar/shared';
import { Icon } from '@/components/Icon';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { isNativeFramework } from '@/features/apps/appFormModel';
import { RepoFilesButton } from '@/features/repo-files/RepoFilesCard';
import { repoFileNames } from '@/features/repo-files/repoFilesModel';
import { Steps, code } from './StepCard';
import { folderLocation } from './stepNotesModel';

/** What each file does, in one line (SPEC decision 23: no provider named; the file names are the client's own). */
const FILE_LINE: Record<string, React.ReactNode> = {
  'app.json': <>{code('app.json')}: the owner, slug and project id Hangar created for the app.</>,
  'app.config.ts': <>{code('app.config.ts')}: the version from {code('HANGAR_VERSION_NAME')} and the build number from {code('HANGAR_BUILD_NUMBER')}.</>,
  'eas.json': <>{code('eas.json')}, the build configuration file: the profiles your build profiles use, with {code('"appVersionSource": "local"')} and Node 22.</>,
  'package.json': <>{code('package.json')}: an {code('eas-build-pre-install')} script that runs {code('hangar runtime env --out .env')}.</>,
  '.easignore': <>{code('.easignore')}: keeps {code('.env')} files out of the upload.</>,
};

/** An Expo / React Native app: the files, and the sheet that fills them for the selected app (SPEC decision 29). */
function ExpoFiles({ app }: { app: App | undefined }) {
  const names = repoFileNames(app?.framework ?? 'expo');
  return (
    <>
      <Steps items={names.map((n) => FILE_LINE[n])} />
      {app ? <RepoFilesButton app={app} testId="gs-repo-files-open" /> : <p className="text-muted-foreground">Pick an app above to see its files filled in.</p>}
    </>
  );
}

/** What a native app needs: nothing outside Hangar; Hangar prepares its builds and workflows (hangar.yml returns with docs/mvp-plan.md P9). */
function NativeFiles() {
  return (
    <Steps
      items={[
        <>The native project builds from the repository as it is (for example {code('apps/android')} with its Gradle wrapper).</>,
        <>Nothing to set up outside Hangar: Hangar prepares the native builds and their workflows (for example {code('android-apk')}).</>,
      ]}
    />
  );
}

/** What the trigger says: the folder and what to prepare in it, and the open/closed chevron. */
function TriggerLabel({ open, native, count, where }: { open: boolean; native: boolean; count: number; where: string }) {
  return (
    <>
      <span className="flex min-w-0 items-center gap-2">
        <Icon name="folder_open" size={18} className="shrink-0 text-subtle" />
        <span className="min-w-0">
          <span className="font-medium text-foreground">Prepare the app folder</span>
          <span className="text-muted-foreground"> · {native ? 'nothing to add' : `${count} small files`} {where} before the first build</span>
        </span>
      </span>
      <Icon name={open ? 'expand_less' : 'expand_more'} size={20} className="shrink-0 text-subtle" />
    </>
  );
}

/**
 * What the app folder needs before its first Hangar build (not visible to the API, so not a tracked step), folded under
 * the apps table: the files of an Expo / React Native app, filled for the selected app in a sheet to copy or download
 * (SPEC decision 29); nothing for a native app (Hangar prepares its builds).
 */
export function RepoChecklist({ app }: { app?: App }) {
  const [open, setOpen] = useState(false);
  const native = Boolean(app && isNativeFramework(app.framework));
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border" data-testid="gs-repo-checklist">
      <CollapsibleTrigger asChild>
        <button type="button" className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg px-3 text-left text-[13px] hover:bg-accent">
          <TriggerLabel open={open} native={native} count={repoFileNames(app?.framework ?? 'expo').length} where={folderLocation(app?.workingDirectory)} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-3 border-t px-3 py-3 text-[13px] leading-relaxed text-foreground">
        {native ? <NativeFiles /> : <ExpoFiles app={app} />}
      </CollapsibleContent>
    </Collapsible>
  );
}
